import { useParams } from "react-router-dom";
import { Flex, Typography } from "antd";
import { useOne, useTranslate } from "@refinedev/core";
import { FilmSlateIcon, StarIcon } from "@phosphor-icons/react";
import { LaunchRow, Skeleton } from "@libertify/ui";
import type { MediaProjectResponse, ProjectMediaResponse } from "pages/media/types";
import { PlaybookAddOnCard } from "../PlaybookAddOnCard";
import { CONFIG_ITEM } from "../../data/addons";

const KEY = "experienceEdit.videoCard";

/**
 * How often to re-read the project while the storyboard / media it needs are
 * still being generated. Shorter than the 30s used for background status badges
 * elsewhere, because here the user is waiting on a button to become clickable.
 */
const GENERATION_POLL_INTERVAL = 10000;

/** Media states that will never progress, so polling should give up. */
const TERMINAL_MEDIA_STATUSES = new Set(["Error", "Cancelled"]);

/**
 * Media states that mean a video exists and can be edited. Kept as plain
 * strings: `Generated` (instance status) and `OutOfDate` are returned by the
 * API but missing from the generated `MediaStatus` type.
 */
const GENERATED_MEDIA_STATUSES = new Set([
  "Ready",
  "Generated",
  "Uploaded",
  "OutOfDate",
]);

/**
 * The ids the two editor routes need. The storyboard is the project's first one
 * (matching the classic project detail page); the media is the one for this
 * instance — matched on the URL's instance when possible, since that URL may
 * carry the "default" alias instead of a real id.
 */
const resolveEditorTargets = (
  project?: MediaProjectResponse,
  instanceId?: string
): { storyboardId?: string; media?: ProjectMediaResponse } => {
  const storyboardId = project?.storyboards?.[0]?.id;
  const media =
    project?.media?.find((m) => m.instance_id === instanceId) ??
    project?.media?.find((m) => m.storyboard_settings_id === storyboardId) ??
    project?.media?.[0];
  return { storyboardId, media };
};

/** New tab, so the experience edit session is never lost. */
const openEditor = (url?: string) => {
  if (url) globalThis.open(url, "_blank", "noopener,noreferrer");
};

export interface VideoEditCardProps {
  /** Whether the video is shown to viewers of the experience. */
  visible?: boolean;
  onVisibleChange?: (visible: boolean) => void;
  visiblePending?: boolean;
}

/**
 * Video card for the experience edit page.
 *
 * Unlike the create-flow `VideoConfigCard` (which collects generation settings
 * before the video exists), the video of an existing experience is edited in two
 * dedicated full-page editors. So this card is purely a launcher: a visibility
 * toggle plus a row per editor.
 */
export const VideoEditCard = ({
  visible,
  onVisibleChange,
  visiblePending,
}: VideoEditCardProps) => {
  const t = useTranslate();
  const { projectId, instanceId } = useParams<{
    projectId: string;
    instanceId?: string;
  }>();

  // Neither editor route is reachable from the experience URL alone: the
  // storyboard editor needs `storyboardId` + a real instance id, and the
  // highlights editor needs `mediaId`. Both are generated asynchronously after
  // the experience is created, so keep re-reading the project until each editor
  // has something to open (an editor can't render a storyboard that isn't
  // generated yet), then stop.
  const { data, isLoading, isError } = useOne<MediaProjectResponse>({
    resource: "media/projects",
    id: projectId ?? "",
    errorNotification: false,
    queryOptions: {
      enabled: Boolean(projectId),
      refetchInterval: (response) => {
        const { storyboardId, media } = resolveEditorTargets(
          response?.data,
          instanceId
        );
        // No media entry at all means nothing is being generated: the entry
        // exists from the start with a null `id` while its media is produced. So
        // this is a project that simply has no media (never will, without a
        // separate action) — polling it would never end.
        if (!media) return false;
        if (TERMINAL_MEDIA_STATUSES.has(media.status)) return false;
        const ready = Boolean(storyboardId && media.instance_id && media.id);
        return ready ? false : GENERATION_POLL_INTERVAL;
      },
    },
  });

  const { storyboardId, media } = resolveEditorTargets(data?.data, instanceId);
  // Built from `media.instance_id` rather than the URL's, so the "default"
  // alias never leaks into the editor route.
  const videoEditorUrl =
    projectId && storyboardId && media?.instance_id
      ? `/media/projects/${projectId}/${storyboardId}/${media.instance_id}/storyboard/editor`
      : undefined;
  const hasGeneratedVideo = Boolean(
    media && GENERATED_MEDIA_STATUSES.has(media.status)
  );
  const highlightsEditorUrl =
    projectId && media?.id
      ? `/media/projects/${projectId}/media/${media.id}/animation`
      : undefined;

  const renderBody = () => {
    if (isLoading) return <Skeleton variant="text" rows={2} />;
    if (isError) {
      return (
        <Typography.Text type="danger">{t(`${KEY}.error`)}</Typography.Text>
      );
    }
    if (!hasGeneratedVideo) {
      return (
        <Typography.Text type="secondary">
          {t(`${KEY}.notGenerated`)}
        </Typography.Text>
      );
    }
    return (
      <Flex vertical gap={12}>
        <LaunchRow
          icon={<FilmSlateIcon weight="duotone" />}
          color="#3B82F6"
          title={t(`${KEY}.videoEditor.title`)}
          description={t(`${KEY}.videoEditor.description`)}
          actionLabel={t(`${KEY}.openEditor`)}
          disabled={!videoEditorUrl}
          onAction={() => openEditor(videoEditorUrl)}
        />
        <LaunchRow
          icon={<StarIcon weight="duotone" />}
          color="#F59E0B"
          title={t(`${KEY}.highlightsEditor.title`)}
          description={t(`${KEY}.highlightsEditor.description`)}
          actionLabel={t(`${KEY}.openEditor`)}
          disabled={!highlightsEditorUrl}
          onAction={() => openEditor(highlightsEditorUrl)}
        />
      </Flex>
    );
  };

  return (
    <PlaybookAddOnCard
      itemId={CONFIG_ITEM.Video}
      title={t(`${KEY}.title`)}
      description={t(`${KEY}.subtitle`)}
      visible={visible}
      visiblePending={visiblePending}
      onVisibleChange={onVisibleChange}
    >
      {renderBody()}
    </PlaybookAddOnCard>
  );
};

export default VideoEditCard;
